import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react'
import { nanoid } from 'nanoid'
import * as R from 'remeda'
import { QueryClientProvider, QueryClient } from '@tanstack/react-query'
import { createGlobalStyle, Provider as ThemeProvider, Theme, Box } from '@chilipiper/design-system'
import {
  XMPPClientProvider,
  RoomDataProvider,
  TimeoutProvider,
  ChiliTheme,
  MessageFeedProvider,
  useSelectPlaybook,
  useChatBootstrap,
  getStoredSession,
  setStoredSession,
  syncStoredGuestId,
  PageVisibilityProvider,
  setWidgetState,
  getWidgetState,
  type AppearanceSettings,
} from '@chilipiper/chat-components'
import { useLogRocket } from '@chilipiper/tracking'
import { DeviceTypeValue } from '@chilipiper/api-client/src/chat-platform-builder'
import { LocaleProvider, locales, Locales, normalizeLanguage } from '@chilipiper/locale/src/new'
import { handlePageActivity } from '../../shared/handlePageActivity'
import { Chatbot } from './Chatbot'
import {
  getWorkspaceId,
  getTenantId,
  getPlaybookId,
  sendMessageToEmbed,
  createEmbedMessageListener,
} from './utils/embedCommunication'
import { ApiClientProvider } from './api-queries/context'
import { useCollectEvent } from './api-queries/chatPlatformChat'
import { useSubmitGuestActivity } from './api-queries/chiliCrm'

const GlobalStyles = createGlobalStyle`
    html, body, #root {
        overflow: hidden;
        height: 100%;
        font-family: ${props => (props.theme as Theme).fonts['primary']};
    }
`

type Utm = {
  campaign: string | undefined
  source: string | undefined
  medium: string | undefined
  term: string | undefined
  content: string | undefined
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 5,
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false,
      staleTime: 30000,
    },
  },
})
const workspaceId = getWorkspaceId()
const tenantId = getTenantId()
const forcedPlaybookId = getPlaybookId()

export const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <ApiClientProvider>
        <AppInner />
      </ApiClientProvider>
    </ThemeProvider>
  </QueryClientProvider>
)

const AppInner = () => {
  const [maxScrollPercentage, setMaxScrollPercentage] = useState<number>()
  const throttledSetScrollPercentage = useRef(
    R.funnel(setMaxScrollPercentage, {
      minGapMs: 300,
      triggerAt: 'both',
      reducer: (_acc, arg: number | undefined) => arg,
    }).call
  )
  const [playbookId, setPlaybookId] = useState<string>(forcedPlaybookId ?? '')
  const [userTimeOnPage, setUserTimeOnPage] = useState<number>()
  const [originUrl, setOriginUrl] = useState<string>()
  const [originUtm, setOriginUtm] = useState<Utm>()
  const [deviceType, setDeviceType] = useState<DeviceTypeValue>()
  const [roomId, setRoomId] = useState<string>()
  const [isNarrowScreen, setIsNarrowScreen] = useState(false)
  const [isPageVisible, setIsPageVisible] = useState(false)
  const guestId = useMemo(() => syncStoredGuestId(workspaceId, tenantId, forcedPlaybookId), [])
  const [botState, setBotState] = useState<BotState | null>(() => getWidgetState(guestId))
  const { mutate: collect } = useCollectEvent(tenantId, guestId)
  const { mutate: submitActivity } = useSubmitGuestActivity(tenantId, guestId)

  const handleBotStateChange = useCallback(
    (botState: BotState) => {
      setBotState(botState)
      setWidgetState(guestId, botState)
    },
    [guestId]
  )

  useLogRocket(guestId ? { id: guestId } : undefined, {
    delayInitOnInteraction: true,
  })

  const handleActivity = useMemo(() => {
    return R.funnel(
      () => {
        const now = new Date().toISOString()
        const session = getStoredSession(guestId, workspaceId)
        if (session?.hasActiveSession) {
          setStoredSession(guestId, workspaceId, now, session.playbookId)
        }
        submitActivity({
          event: {
            type: 'SimpleActivityEvent',
            id: nanoid(),
            timestamp: now,
            eventType: 'COMMON.WEBSITE_ACTIVITY',
          },
        })
      },
      { minGapMs: 10 * 1000, triggerAt: 'start' }
    ).call
  }, [guestId, submitActivity])

  useEffect(() => {
    return handlePageActivity(handleActivity)
  }, [handleActivity])

  useEffect(() => {
    return createEmbedMessageListener(message => {
      switch (message.type) {
        case 'UPDATE_ORIGIN_FULL_URL': {
          if (!originUrl) {
            setOriginUrl(message.value)
          }
          const query = new URL(message.value).searchParams
          const utm = {
            campaign: query.get('utm_campaign') ?? undefined,
            source: query.get('utm_source') ?? undefined,
            medium: query.get('utm_medium') ?? undefined,
            term: query.get('utm_term') ?? undefined,
            content: query.get('utm_content') ?? undefined,
          }
          if (
            (utm.campaign && utm.campaign !== originUtm?.campaign) ||
            (utm.source && utm.source !== originUtm?.source) ||
            (utm.medium && utm.medium !== originUtm?.medium) ||
            (utm.term && utm.term !== originUtm?.term) ||
            (utm.content && utm.content !== originUtm?.content)
          ) {
            setOriginUtm(utm)
            submitActivity({
              event: {
                type: 'UtmActivityEvent',
                id: nanoid(),
                utmParameters: utm,
                timestamp: new Date().toISOString(),
              },
            })
          }
          collect({ type: 'PageViewed', url: message.value })
          break
        }
        case 'UPDATE_MAX_SCROLL_PERCENTAGE': {
          throttledSetScrollPercentage.current(message.value)
          break
        }
        case 'TRIGGER_CHAT': {
          setPlaybookId(message.value.journeyId)
          handleBotStateChange('expanded')
          break
        }
        case 'UPDATE_DEVICE_TYPE': {
          setDeviceType(message.value)
          if (!botState) {
            handleBotStateChange(message.value === 'Mobile' ? 'collapsed' : 'preview')
          }
          break
        }
        case 'UPDATE_USER_TIME_ON_PAGE': {
          setUserTimeOnPage(message.value)
          break
        }
        case 'IS_NARROW_SCREEN_CHANGE': {
          setIsNarrowScreen(message.value)
          break
        }
        case 'PAGE_VISIBILITY_CHANGE': {
          setIsPageVisible(message.value)
          break
        }
        case 'GUEST_ACTIVITY': {
          handleActivity()
          break
        }
      }
    })
  }, [collect, guestId, handleActivity, originUrl])

  useEffect(() => {
    sendMessageToEmbed({ type: 'WIDGET_COMMUNICATION_READY' })
  }, [])

  const handleBootstrapComplete = useCallback(
    (roomId: string, playbookId: string) => {
      sendMessageToEmbed({ type: 'STOP_LISTENING_FOR_LOCATION_DATA' })
      setStoredSession(guestId, workspaceId, new Date().toISOString(), playbookId)
      return setRoomId(roomId)
    },
    [guestId]
  )

  const handleSetTriggers = (triggers: AppearanceSettings['customTriggers']['published']) => {
    sendMessageToEmbed({ type: 'SET_TRIGGERS', value: triggers })
  }

  const handleAppearanceSettingsChange = (settings: AppearanceSettings) => {
    sendMessageToEmbed({ type: 'APPEARANCE_SETTINGS_CHANGE', value: settings })
  }

  const queryLocale = normalizeLanguage(navigator.language, locales)
  const locationData = useMemo(
    () => ({
      originUrl: originUrl ?? '',
      maxScrollPercentage: maxScrollPercentage ?? 0,
      userTimeOnPage: userTimeOnPage ?? 0,
      deviceType: deviceType ?? 'Desktop',
      browserLanguage: queryLocale,
    }),
    [originUrl, maxScrollPercentage, userTimeOnPage, deviceType, queryLocale]
  )
  //TODO change default '' roomId to some meaningful value and ensure proper loading states
  return (
    <ChiliTheme
      workspaceId={workspaceId}
      tenantId={tenantId}
      handleSetTriggers={handleSetTriggers}
      handleAppearanceSettingsChange={handleAppearanceSettingsChange}
    >
      <GlobalStyles />
      <LocaleProvider locale={queryLocale}>
        <XMPPClientProvider>
          <RoomDataProvider isMine roomId={roomId || ''}>
            <PageVisibilityProvider value={isPageVisible}>
              <ChatWithMessageProvider
                isNarrowScreen={isNarrowScreen}
                guestId={guestId}
                workspaceId={workspaceId}
                playbookId={playbookId}
                locationData={locationData}
                onBootstrapComplete={handleBootstrapComplete}
                botState={botState ?? 'preview'}
                onBotStateChange={handleBotStateChange}
              />
            </PageVisibilityProvider>
          </RoomDataProvider>
        </XMPPClientProvider>
      </LocaleProvider>
    </ChiliTheme>
  )
}

type BotState = 'preview' | 'collapsed' | 'expanded'

interface Props {
  botState: BotState
  onBotStateChange: (botState: BotState) => void
  guestId: string
  isNarrowScreen: boolean
  playbookId: string
  workspaceId: string
  locationData: {
    deviceType: DeviceTypeValue
    maxScrollPercentage: number
    originUrl: string
    userTimeOnPage: number
    browserLanguage: Locales
  }
  onBootstrapComplete: (roomId: string, playbookId: string) => void
}

const ChatWithMessageProvider = ({
  guestId,
  locationData,
  workspaceId,
  playbookId,
  onBootstrapComplete,
  isNarrowScreen,
  botState,
  onBotStateChange,
}: Props) => {
  const data = useChatBootstrap({
    workspaceId,
    tenantId,
    guestId,
    locationData,
    forcedPlaybookId: playbookId,
  })

  const { selectConversation } = useSelectPlaybook({
    workspaceId,
    guestId,
    originUrl: locationData.originUrl,
    tenantId,
  })

  return (
    <Box as='section' h='100%' aria-label='Chat widget'>
      <MessageFeedProvider
        isNarrowScreen={isNarrowScreen}
        workspaceId={workspaceId}
        tenantId={tenantId}
        onBootstrapComplete={onBootstrapComplete}
      >
        <TimeoutProvider>
          <Chatbot
            botState={botState}
            onBotStateChange={onBotStateChange}
            guestId={guestId}
            workspaceId={workspaceId}
            conversationData={data?.type === 'NEW_JOURNEY' ? data.conversationData : undefined}
            isBootstrapped={data !== undefined}
            playbookId={data?.playbookId}
            onSessionRestart={(options?: { force: boolean }) => {
              if (data?.playbookId) {
                selectConversation([data.playbookId], options?.force)
                setStoredSession(guestId, workspaceId, new Date().toISOString(), data.playbookId)
              } else {
                console.error('No playbookId found')
              }
            }}
          />
        </TimeoutProvider>
      </MessageFeedProvider>
    </Box>
  )
}
